import React, { useState, useEffect, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AlertTriangle, Info, Loader2 } from 'lucide-react'
import { formatInvoiceDisplayName } from '../../utils/invoiceUtils.js'
import { getTodayISOString } from '../../utils/dateUtils.js'
import { supabase, markInvoiceAsPaid } from '../../supabase-client'

/**
 * MarkAsPaidDialog Component
 *
 * Enhanced payment recording dialog supporting:
 * - Full payments (pay exact amount due)
 * - Partial payments (pay less than amount due)
 * - Legacy invoice handling (creates settlement record for proper audit trail)
 * - Xero invoice detection (disables manual payment for Xero-synced invoices)
 *
 * @param {Object} props
 * @param {Object} props.invoice - Invoice object with id, invoice_no, total, customer, etc.
 * @param {boolean} props.open - Whether dialog is open
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {function} props.onSuccess - Callback after successful payment recording
 */
export default function MarkAsPaidDialog({
  invoice,
  open,
  onOpenChange,
  onSuccess
}) {
  // Balance data from invoice_balances view
  const [balanceData, setBalanceData] = useState(null)
  const [loadingBalance, setLoadingBalance] = useState(false)
  const [balanceError, setBalanceError] = useState(null)

  // Payment form state
  const [paymentMode, setPaymentMode] = useState('full') // 'full' | 'partial'
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentDate, setPaymentDate] = useState(getTodayISOString())
  const [paymentMethod, setPaymentMethod] = useState('Bank Transfer')
  const [paymentReference, setPaymentReference] = useState('')

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  // Fetch balance data when dialog opens
  const fetchBalanceData = useCallback(async () => {
    if (!invoice?.id) return

    setLoadingBalance(true)
    setBalanceError(null)

    try {
      const { data, error } = await supabase
        .from('invoice_balances')
        .select('*')
        .eq('invoice_id', invoice.id)
        .single()

      if (error) throw error

      setBalanceData(data)
      // Default payment amount to amount due
      setPaymentAmount(data.amount_due?.toString() || invoice.total?.toString() || '')
    } catch (err) {
      console.error('[MarkAsPaidDialog] Error fetching balance:', err)
      setBalanceError('Failed to load invoice balance')
      // Fall back to invoice total
      setPaymentAmount(invoice.total?.toString() || '')
    } finally {
      setLoadingBalance(false)
    }
  }, [invoice?.id, invoice?.total])

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open && invoice) {
      fetchBalanceData()
      setPaymentMode('full')
      setPaymentDate(getTodayISOString())
      setPaymentMethod('Bank Transfer')
      setPaymentReference('')
      setSubmitError(null)
    }
  }, [open, invoice, fetchBalanceData])

  // Update amount when payment mode changes
  useEffect(() => {
    if (balanceData) {
      if (paymentMode === 'full') {
        setPaymentAmount(balanceData.amount_due?.toString() || '')
      }
      // For partial mode, keep current amount (user will edit)
    }
  }, [paymentMode, balanceData])

  const handleSubmit = async () => {
    if (!invoice?.id) return

    const amount = parseFloat(paymentAmount)
    if (isNaN(amount) || amount <= 0) {
      setSubmitError('Please enter a valid payment amount')
      return
    }

    const amountDue = balanceData?.amount_due ?? invoice.total
    if (amount > amountDue) {
      setSubmitError(`Payment amount cannot exceed amount due (${formatCurrency(amountDue)})`)
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Call the mark-paid handler via Edge Function
      await markInvoiceAsPaid(invoice.id, {
        payment_date: paymentDate,
        amount: amount,
        reference: paymentReference || undefined,
        method: paymentMethod,
        // Pass legacy flag so handler knows to create settlement first
        is_legacy_paid: balanceData?.is_legacy_paid || false
      })

      // Success - close dialog and notify parent
      onOpenChange(false)
      if (onSuccess) {
        onSuccess()
      }
    } catch (err) {
      console.error('[MarkAsPaidDialog] Payment error:', err)
      setSubmitError(err.message || 'Failed to record payment')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatCurrency = (amount) => {
    if (amount == null) return '-'
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount)
  }

  // Determine if this is a Xero-synced invoice
  const isXeroInvoice = invoice?.xero_invoice_id != null

  // Calculate remaining balance after payment
  const remainingAfterPayment = balanceData
    ? balanceData.amount_due - (parseFloat(paymentAmount) || 0)
    : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            Record a payment against this invoice
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Invoice Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Invoice</span>
              <span className="text-sm font-medium">
                {invoice ? formatInvoiceDisplayName(invoice) : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Customer</span>
              <span className="text-sm font-medium">
                {invoice?.customer?.name || 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between border-t border-border pt-2 mt-2">
              <span className="text-sm text-muted-foreground">Invoice Total</span>
              <span className="text-sm font-medium">
                {formatCurrency(invoice?.total)}
              </span>
            </div>
            {loadingBalance ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading balance...
              </div>
            ) : balanceData ? (
              <>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Amount Paid</span>
                  <span className="text-sm font-medium text-green-600">
                    {formatCurrency(balanceData.amount_paid)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Amount Due</span>
                  <span className="text-sm font-bold text-primary">
                    {formatCurrency(balanceData.amount_due)}
                  </span>
                </div>
              </>
            ) : null}
          </div>

          {/* Xero Invoice Info */}
          {isXeroInvoice && (
            <Alert className="border-blue-200 bg-blue-50">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertTitle className="text-blue-800">Xero-Synced Invoice</AlertTitle>
              <AlertDescription className="text-blue-700">
                This payment will be recorded directly in Xero.
              </AlertDescription>
            </Alert>
          )}

          {/* Legacy Invoice Info */}
          {balanceData?.is_legacy_paid && !isXeroInvoice && (
            <Alert className="border-amber-200 bg-amber-50">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Legacy Invoice</AlertTitle>
              <AlertDescription className="text-amber-700">
                This invoice was marked as paid before payment tracking existed.
                Recording a new payment will create a settlement record for the
                original amount to maintain a proper audit trail.
              </AlertDescription>
            </Alert>
          )}

          {/* Balance Error */}
          {balanceError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{balanceError}</AlertDescription>
            </Alert>
          )}

          {/* Payment Form */}
          <>
            {/* Payment Mode Selection */}
              <div className="space-y-2">
                <Label>Payment Type</Label>
                <RadioGroup
                  value={paymentMode}
                  onValueChange={setPaymentMode}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="full" id="payment-full" />
                    <Label htmlFor="payment-full" className="font-normal cursor-pointer">
                      Full Payment ({formatCurrency(balanceData?.amount_due ?? invoice?.total)})
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="partial" id="payment-partial" />
                    <Label htmlFor="payment-partial" className="font-normal cursor-pointer">
                      Partial Payment
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Payment Amount */}
              <div className="space-y-2">
                <Label htmlFor="payment-amount">Payment Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    &pound;
                  </span>
                  <Input
                    id="payment-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={balanceData?.amount_due ?? invoice?.total}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="pl-7"
                    disabled={paymentMode === 'full'}
                    aria-describedby={paymentMode === 'partial' && remainingAfterPayment > 0 ? 'remaining-balance' : undefined}
                  />
                </div>
                {paymentMode === 'partial' && remainingAfterPayment !== null && remainingAfterPayment > 0 && (
                  <p id="remaining-balance" className="text-sm text-muted-foreground" aria-live="polite">
                    Remaining balance after payment: {formatCurrency(remainingAfterPayment)}
                  </p>
                )}
              </div>

              {/* Payment Date */}
              <div className="space-y-2">
                <Label htmlFor="payment-date">Payment Date</Label>
                <Input
                  id="payment-date"
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <Label htmlFor="payment-method">Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger id="payment-method">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="GoCardless">GoCardless</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Reference */}
              <div className="space-y-2">
                <Label htmlFor="payment-reference">Reference (optional)</Label>
                <Input
                  id="payment-reference"
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="e.g., Bank transfer ref #12345"
                />
              </div>
          </>

          {/* Submit Error */}
          {submitError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !paymentAmount || parseFloat(paymentAmount) <= 0}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Recording...
              </>
            ) : (
              'Record Payment'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
